/**
 * A visitor's work, kept in their own browser.
 *
 * Anyone may design; only an account may save. So until a visitor signs in,
 * nothing they make goes to the shop - the design is written here instead, and
 * so are the pictures they add. When they sign in, the editor comes back with
 * `resume` on the URL and moveIntoAccount() hands the whole thing over: the
 * design is created in their account, each picture is uploaded to it, and the
 * layers are pointed at the uploads.
 *
 * Two stores, because the two things are different sizes:
 *
 *   localStorage  The document, as JSON. Small, and wanted synchronously on the
 *                 way out of the page.
 *   IndexedDB     The pictures, as the files the visitor chose. A phone photo
 *                 is several megabytes, which localStorage will not hold.
 *
 * Keyed by product and variation, because that is the card being designed: a
 * visitor who starts a business card and a flyer has two drafts, not one that
 * keeps being replaced.
 */

import type { Asset } from './assets';
import type { Design, Layer, TrimboxDocument } from './model/types';

const DRAFT_PREFIX = 'trimbox.draft.';
const PENDING_PREFIX = 'trimbox.pending.';

/** Ids of pictures that live here rather than on the server. */
const LOCAL_PREFIX = 'local-';

/** What a visitor meant to do when they were asked to sign in. */
export type Pending = 'save' | 'basket' | 'designs' | 'proof';

export interface Draft {
  doc: TrimboxDocument;
  title: string;
  /** When it was written, ms. */
  at: number;
}

export function draftKey(product: number, variation: number): string {
  return `${product}.${variation}`;
}

export function readDraft(key: string): Draft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_PREFIX + key);
    const draft = raw ? (JSON.parse(raw) as Draft) : null;

    return draft && draft.doc && Array.isArray(draft.doc.pages) ? draft : null;
  } catch {
    return null;
  }
}

/** @returns false when the browser would not keep it - full, or refused. */
export function writeDraft(key: string, draft: Draft): boolean {
  try {
    window.localStorage.setItem(DRAFT_PREFIX + key, JSON.stringify(draft));

    return true;
  } catch {
    return false;
  }
}

export function dropDraft(key: string): void {
  try {
    window.localStorage.removeItem(DRAFT_PREFIX + key);
    window.localStorage.removeItem(PENDING_PREFIX + key);
  } catch {
    // Nothing to do: a draft that cannot be removed cannot be read either.
  }
}

export function setPending(key: string, what: Pending): void {
  try {
    window.localStorage.setItem(PENDING_PREFIX + key, what);
  } catch {
    // Forgotten, which only means the customer presses the button again.
  }
}

/** Reads and forgets it: a pending action happens once. */
export function takePending(key: string): Pending | null {
  try {
    const what = window.localStorage.getItem(PENDING_PREFIX + key);

    window.localStorage.removeItem(PENDING_PREFIX + key);

    return what === 'save' || what === 'basket' || what === 'designs' || what === 'proof' ? what : null;
  } catch {
    return null;
  }
}

/**
 * A design that is not stored anywhere yet.
 *
 * An empty uuid is what says so. A visitor's design is always one of these; a
 * signed-in customer's is one until its first save, so opening the editor and
 * closing it again does not leave an "Untitled design" in their list.
 */
export function unsavedDesign(doc: TrimboxDocument, title = ''): Design {
  return {
    uuid: '',
    title,
    status: 'draft',
    product: doc.product.id,
    variation: doc.product.variation,
    orderItem: 0,
    thumb: '',
    created: '',
    updated: '',
    document: doc,
  };
}

/* ---------------------------------------------------------------
 * Pictures
 * ------------------------------------------------------------ */

export const isLocalAsset = (id: string) => id.startsWith(LOCAL_PREFIX);

/** The same limits the server applies - see Trimbox_Assets. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 40 * 1024 * 1024;
const MIN_SIDE = 16;
const MAX_SIDE = 12000;

interface Stored {
  id: string;
  blob: Blob;
  name: string;
  w: number;
  h: number;
  mime: string;
}

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open('trimbox', 1);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains('pictures')) {
        request.result.createObjectStore('pictures', { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await database();

  return new Promise((resolve, reject) => {
    const request = work(db.transaction('pictures', mode).objectStore('pictures'));

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Blob URLs made this session, one per picture, so each is made once. */
const urls = new Map<string, string>();

function asAsset(stored: Stored): Asset {
  let url = urls.get(stored.id);

  if (!url) {
    url = URL.createObjectURL(stored.blob);
    urls.set(stored.id, url);
  }

  return {
    id: stored.id,
    kind: 'image',
    name: stored.name,
    url,
    w: stored.w,
    h: stored.h,
    bytes: stored.blob.size,
    mime: stored.mime,
  };
}

/**
 * Keeps a picture in the browser, judged by the rules the server will judge it
 * by later - so a visitor is told now that a file will not do, not after they
 * have signed in and the upload is refused.
 */
export async function keepPicture(file: File): Promise<Asset> {
  if (!ACCEPTED.includes(file.type)) {
    throw new Error('We cannot use that kind of file yet. JPG, PNG and WebP all work.');
  }

  if (file.size > MAX_BYTES) {
    throw new Error('That picture is larger than 40 MB. Please send us a smaller one.');
  }

  const size = await measure(file);

  if (!size) throw new Error('That file is not a picture we can read. JPG, PNG and WebP all work.');
  if (size.w < MIN_SIDE || size.h < MIN_SIDE) throw new Error('That picture is too small to print. Please send a larger one.');
  if (size.w > MAX_SIDE || size.h > MAX_SIDE) {
    throw new Error(`That picture is larger than ${MAX_SIDE} pixels on a side. Please send a smaller one.`);
  }

  const id = LOCAL_PREFIX + Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');
  const stored: Stored = { id, blob: file, name: file.name, w: size.w, h: size.h, mime: file.type };

  try {
    await withStore('readwrite', (store) => store.put(stored));
  } catch {
    throw new Error('Your browser would not keep that picture. Sign in to upload it to your account instead.');
  }

  return asAsset(stored);
}

async function measure(file: Blob): Promise<{ w: number; h: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { w: bitmap.width, h: bitmap.height };

    bitmap.close();

    return size;
  } catch {
    return null;
  }
}

/** The kept pictures a document uses, ready to register with the canvas. */
export async function keptPictures(doc: TrimboxDocument): Promise<Asset[]> {
  const out: Asset[] = [];

  for (const id of localIds(doc)) {
    const stored = await withStore<Stored | undefined>('readonly', (store) => store.get(id)).catch(() => undefined);

    if (stored) out.push(asAsset(stored));
  }

  return out;
}

export async function keptFile(id: string): Promise<File | null> {
  const stored = await withStore<Stored | undefined>('readonly', (store) => store.get(id)).catch(() => undefined);

  return stored ? new File([stored.blob], stored.name || 'picture', { type: stored.mime }) : null;
}

export async function forgetPicture(id: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(id)).catch(() => undefined);

  const url = urls.get(id);

  if (url) URL.revokeObjectURL(url);

  urls.delete(id);
}

/** Every kept picture a document names, in its list or on a layer. */
export function localIds(doc: TrimboxDocument): string[] {
  const ids = new Set<string>();

  for (const ref of doc.assets) if (isLocalAsset(ref.id)) ids.add(ref.id);

  const walk = (layers: Layer[]) => {
    for (const layer of layers) {
      if (layer.type === 'image' && layer.asset && isLocalAsset(layer.asset)) ids.add(layer.asset);
      if (layer.type === 'text' && layer.picture?.asset && isLocalAsset(layer.picture.asset)) ids.add(layer.picture.asset);
      if (layer.type === 'group') walk(layer.children);
    }
  };

  for (const page of doc.pages) walk(page.layers);

  return Array.from(ids);
}

/**
 * The document with each kept picture swapped for its upload.
 *
 * A picture that could not be uploaded keeps its local id, and the layer draws
 * as an empty frame - which the preflight then reports, rather than the design
 * quietly losing a photograph.
 */
export function withUploads(doc: TrimboxDocument, uploads: Map<string, Asset>): TrimboxDocument {
  if (uploads.size === 0) return doc;

  const swap = (layers: Layer[]): Layer[] =>
    layers.map((layer) => {
      if (layer.type === 'image' && layer.asset && uploads.has(layer.asset)) {
        return { ...layer, asset: uploads.get(layer.asset)!.id };
      }

      if (layer.type === 'text' && layer.picture?.asset && uploads.has(layer.picture.asset)) {
        return { ...layer, picture: { ...layer.picture, asset: uploads.get(layer.picture.asset)!.id } };
      }

      if (layer.type === 'group') return { ...layer, children: swap(layer.children) };

      return layer;
    });

  return {
    ...doc,
    pages: doc.pages.map((page) => ({ ...page, layers: swap(page.layers) })),
    assets: doc.assets.map((ref) => {
      const upload = uploads.get(ref.id);

      return upload ? { ...ref, id: upload.id, ref: upload.id, w: upload.w, h: upload.h } : ref;
    }),
  };
}
